<template lang="html">
  <div>
    <div v-if="loading">
      Loading . . .
    </div>
    <div v-if="noResults">No results, try expanding you search query</div>
    <div id="SquadMap"
    ref="map"
    class="SquadMap">
      <map-popup-squad
      ref="popup"
      :zipcode="popupData.zip"
      :distance="popupData.distance"
      :squads="squadsData">
      </map-popup-squad>
    </div>
  </div>
</template>

<script>
// Libraries
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import lineDistance from "turf-line-distance";
// import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import MapboxClient from "mapbox/lib/services/geocoding";
import { flattenDeep, find } from "lodash";
// Components
import MapPopupSquad from "../components/map-popup-squad";

// @NOTE If you update the environment variable, it will also require the application
// to be recompiled. The fastest way around this is to push an empty commit.
// https://stackoverflow.com/questions/9713183/recompile-heroku-slug-without-push-or-config-change#answer-12269801
const client = new MapboxClient(process.env.MAPBOX_ACCESS_TOKEN);
const accessToken = process.env.MAPBOX_ACCESS_TOKEN;
const mapStyle = `mapbox://styles/mapbox/${process.env.MAPBOX_MAP_STYLE}`;
let resultsTimeout;

export default {
  components: {
    MapPopupSquad
  },

  data() {
    return {
      loading: false,
      centerCoords: [-98.5795, 39.8283],
      // bounds: [[-180, 20], [-30, 72]],
      popupData: {},
      noResults: false,
      zipData: {},
      mapPopup: null
    };
  },

  props: {
    query: "",
    geoLocation: null,
    bounds: {
      required: true,
      type: Array,
      default: [[-180, 20], [-30, 72]]
    },
    mapApiUrl: {
      required: true,
      type: String
    }
  },

  watch: {
    query(newVal, oldVal) {
      !!newVal && this.geocodeQuery(newVal)
    },
    geoLocation(newVal, oldVal) {
      // !!newVal && this.mapFlyToLocation(this.createCenterFromGeolocation(newVal)) // To skip geocoding users location
      let location;
      if (!!newVal) {
        location = typeof newVal === 'string' ? JSON.parse(newVal) : newVal;
      }
      !!newVal && this.geocodeUserLocation(location) // To skip geocoding users location
    }
  },

  computed: {
    squadsData() {
      if (!!this.popupData.squads) {
        const data = (typeof this.popupData.squads === "string" ? JSON.parse(this.popupData.squads) : this.popupData.squads)
        return data;
      } else {
        return null
      }
    }
  },

  /**
   * Request data from the API
   */
  mounted() {
    const self = this;
    this.loading = true;
    self.$http.get(self.mapApiUrl).then(
      response => {
        // get body data
        self.zipData = response.body;
        self.loading = false;
        self.initializeMap();
      },
      response => {
        console.warn(response);
        // error callback
      }
    );
  },

  methods: {
    /**
     * Create the map instance
     */
    initializeMap() {
      const self = this;
      mapboxgl.accessToken = accessToken;

      self.map = new mapboxgl.Map({
        container: self.$refs.map.getAttribute("id"),
        style: mapStyle,
        center: self.centerCoords,
        maxBounds: self.bounds,
        zoom: 3,
        maxZoom: 11,
        minZoom: 3,
        scrollZoom: false,
        dragRotate: false,
        touchZoomRotate: false,
        attributionControl: false
      });

      self.map.addControl(
        new mapboxgl.AttributionControl({
          compact: true
        })
      );

      self.map.addControl(new mapboxgl.NavigationControl(), "top-left");

      self.map.on("load", function() {
        self.map.addSource("squads", {
          type: "geojson",
          data: self.zipData,
          cluster: true,
          clusterMaxZoom: 8,
          clusterRadius: 50
        });

        self.map.addLayer({
          id: "clusters",
          type: "circle",
          source: "squads",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": {
              property: "point_count",
              type: "interval",
              stops: [[0, "#38a5d6"]]
            },
            "circle-radius": {
              property: "point_count",
              type: "interval",
              stops: [[0, 14]]
            }
          }
        });

        self.map.addLayer({
          id: "cluster-count",
          type: "symbol",
          source: "squads",
          filter: ["has", "point_count"],
          layout: {
            "text-field": "{point_count_abbreviated}",
            // "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12
          }
        });

        self.map.addLayer({
          id: "unclustered-point",
          type: "circle",
          source: "squads",
          filter: ["!has", "point_count"],
          paint: {
            "circle-color": "#38a5d6",
            "circle-radius": 6,
            "circle-stroke-width": 0,
            "circle-stroke-color": "#000"
          }
        });

        // self.map.addControl(
        //   new MapboxGeocoder({
        //     accessToken: mapboxgl.accessToken
        //   })
        // );

        self.mapAddPopupEvent();

        if (typeof self.query !== 'undefined' && self.query !== "") {
          self.geocodeQuery(self.query);
        } else if (self.geoLocation !== null) {
          self.geocodeUserLocation(typeof self.geoLocation === 'string' ? JSON.parse(self.geoLocation) : self.geoLocation);
        }
      });
    },

    /**
     * Search a location from query string
     * @param {string} query - address, keyword or zipcode used to query search API
     * if results are found, the first result is used to navigate the map.
     * if the results provides a zipcode that matches a squad, the corresponding
     * popup will be opened.
     */
    geocodeQuery(query) {
      const self = this;
      // Request results from Mapbox's Geocoder API
      // docs: https://github.com/mapbox/mapbox-sdk-js/blob/master/API.md#geocodeforward
      client
        .geocodeForward(query, {
          bbox: flattenDeep(this.bounds),
          country: "US",
          types: "region,postcode,place,neighborhood"
        })
        .then(res => {
          const query = res.entity.query[0];
          const results = res.entity.features;

          // Has results
          if (results.length) {
            const firstResult = results[0];
            self.mapFlyToLocation(firstResult);
            // Update data to include distance
            self.updateFuzzyDistances(firstResult.center);
            // Remove the popup if one exists
            !!this.mapPopup && this.mapPopup.remove();
            // Check for a matching squad zipcode to the match
            const match = find(this.zipData.features, squad => {
              return squad.properties.zip === query.trim();
            })
            if (match) {
              self.popupData = match.properties;
              self.mapCreatePopup(match);
            }
          } else {
            console.warn("NO RESULTS");
            self.setNoResults();
          }
        })
        .catch(err => {
          // FIXME: Need to handle errors
          console.log(err);
        });
    },

    /**
     * Query zipcode from query string
     * @param {object} location - geolocation coordinates passed to the search API
     * if results are found, the first result is used to navigation the map.
     * if the results include a zipcode that matches a squad, the corresponding
     * popup will be opened.
     */
    geocodeUserLocation(location) {
      const self = this;
      // Browser API data doesn't match search API structure and needs reformatted
      location = {
        latitude: location.lat,
        longitude: location.lng
      };
      // Request results from Mapbox's Geocoder API
      // docs: https://github.com/mapbox/mapbox-sdk-js/blob/master/API.md#geocodereverse
      client
        .geocodeReverse(location, {
          types: "postcode"
        })
        .then(res => {
          const query = res.entity.query[0];
          const results = res.entity.features;
          if (results.length) {
            const firstResult = results[0];
            self.mapFlyToLocation(firstResult);
            // Update data to include distance
            self.updateFuzzyDistances(firstResult.center);
            // Remove the popup if one exists
            !!this.mapPopup && this.mapPopup.remove();
            // Check for a match amoun the squad data
            const match = find(this.zipData.features, squad => {
              return squad.properties.zip === results[0].text.trim();
            })
            if (match) {
              self.popupData = match.properties;
              self.mapCreatePopup(match);
            }
          } else {
            console.warn("NO RESULTS");
            self.setNoResults();
          }
        })
        .catch(err => {
          // FIXME: Need to handle errors
          console.log(err);
        });
    },

    /**
     * Register map click event to trigger pop-up to open.
     */
    mapAddPopupEvent() {
      const self = this;
      // When a click event occurs on a feature in the places layer, open a popup at the
      // location of the feature, with description HTML from its properties.
      this.map.on("click", "unclustered-point", function(e) {
        // Find the squad with matching zipcode to popupate the popup data
        const squad = find(self.zipData.features, squad => {
          return squad.properties.zip === e.features[0].properties.zip
        })
        self.popupData = squad.properties;
        self.mapCreatePopup(squad)
      });
    },

    /**
     * Create map popup
     * @param {object} squad - data containing location coordinates
     * popup DOM content is populated with a vue element $reference
     */
    mapCreatePopup(squad) {
      this.mapPopup = new mapboxgl.Popup()
        .setLngLat(squad.geometry.coordinates)
        .setDOMContent(this.$refs.popup.$el) // markup is generated with vue
        .addTo(this.map);
    },

    /**
     * Fly Map to a Location
     * @param {object} location - contains attributes to provide the map for navigation
     * depending on what structure is passed, we either fit the map to certain
     * bounds or navigate to a specific center point. This accomidates different
     * location formats return from the geolocation search
     */
    mapFlyToLocation(location) {
      // Check for bounding box
      if (typeof location.bbox !== 'undefined') {
        this.map.fitBounds([
          [location.bbox[0], location.bbox[1]],
          [location.bbox[2], location.bbox[3]]
        ]);
      } else {
        // Otherwise use the center location to flyTo
        this.map.flyTo(location);
      }
    },

    /**
     * Create center object array from geolocation data
     * @param {object} locaiton - data return when requesting a users geolocation
     * using the browsers geolocation API
     */
    createCenterFromGeolocation(location) {
      try {
        return {
          center: [location.lng, location.lat]
        }
      } catch (e) {
        console.warn('Geolocation data supplied in an unsupported format')
      }
    },

    /**
     * Set no results return from Search
     */
    setNoResults() {
      this.noResults = true;
      clearTimeout(resultsTimeout);
      resultsTimeout = setTimeout( () => {
        this.noResults = false;
      }, 3000);
    },

    /**
     * Generate distance between locations
     * @param {array} pos - coordinates for point to reference
     */
    updateFuzzyDistances(pos) {
      this.zipData.features.forEach(squad => {
        const coordinates = squad.geometry.coordinates;
        // Scaffold a GeoJSON object
        const line = {
          "type": "Feature",
          // "properties": {},
          "geometry": {
            "type": "LineString",
            "coordinates": [
              // longitude, latitude
              [pos[0], pos[1]],
              coordinates
            ]
          }
        };

        // Get the distance in miles
        const distance = lineDistance(line, 'miles');
        // Collect the current properties value on the marker model
        squad.properties.distance = parseFloat(distance.toFixed(2));;
      })
    }
  }
};

</script>



// WEBPACK FOOTER //
// squad-map.vue?671752b4